import { refineMediaDevices } from 'features/user-media/utils/refineMediaDevices';
import webrtcAdapter from 'webrtc-adapter';
import {
  addDeviceChangeListener,
  enumerateDevices,
  getDefaultActiveDevices,
  getDefaultMediaDevice,
  getDevicePermissions,
  getDevicePromptConstraints,
  removeDeviceChangeListener,
  stopStreamTracks,
} from 'features/user-media/utils';
import { getUserMedia } from 'features/user-media/utils/getUserMedia';
import { roomJoinRequest } from 'features/room/actions';
import { useMediaDevices } from 'hooks/useMediaDevices';
import { EnumeratedMediaDevices } from 'features/user-media/types';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  activeMediaDeviceUpdated,
  facingModeChanged,
  mediaDevicesUpdated,
  selectJoinMediaDefaults,
} from 'features/user-media/userMediaSlice';
import { isDomException } from 'utils/types';
import { logger } from 'utils/logger';
import { getDefaultPromptedDevice } from 'features/user-media/utils/getDefaultPromptedDevice';
import * as Sentry from '@sentry/react';
import { useSingularUserMedia } from 'hooks/useSingularUserMedia';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import debounce from 'lodash.debounce';
import { findDeviceDifferences } from 'features/user-media/utils/findDeviceDifferences';
import { useBroadcastSetupMedia } from 'utils/broadcast-setup';
import { usePublishingFeed } from 'hooks/usePublishingFeed';
import { PublishingFeed } from 'utils/webrtc/publishing/PublishingFeed';
import { selectRoomToken } from 'features/room/roomSlice';
import { selectSDKInitState } from 'features/sdk/sdkStateSlice';
import { usePermissions } from 'features/permissions/usePermissions';
import { PermissionTypes } from 'features/permissions/types';

export const useFastJoinMedia = () => {
  const appDispatch = useAppDispatch();

  const {
    audioStream,
    videoStream,
    dispatch,
    state,
    enableAudio,
    enableCamera,
    disableCamera,
    disableAudio,
    pendingMedia,
  } = useBroadcastSetupMedia({
    reason: 'join',
  });

  const roomToken = useAppSelector(selectRoomToken);
  const sdkInitState = useAppSelector(selectSDKInitState);

  const mediaDefaults = useAppSelector(selectJoinMediaDefaults);
  const { updateMediaDevices, activeMediaDevices, mediaDevices, micPermissions, camPermissions } =
    useMediaDevices();

  const requestSingularUserMedia = useSingularUserMedia();

  const { hasPermissions } = usePermissions();
  const allowVideoBroadcast = hasPermissions(PermissionTypes.broadcastVideo);
  const allowAudioBroadcast = hasPermissions(PermissionTypes.broadcastAudio);

  const broadcastPermissions = {
    video: allowVideoBroadcast,
    audio: allowAudioBroadcast,
  };

  const feed = usePublishingFeed('media');
  const publishingFeed = feed.control as PublishingFeed;

  const deactivateMic = useCallback(() => {
    dispatch({ type: 'mediaDeactivated', payload: 'audio' });
  }, [dispatch]);

  const deactivateCamera = useCallback(() => {
    dispatch({ type: 'mediaDeactivated', payload: 'video' });
  }, [dispatch]);

  // Store frequently changing values in refs to avoid re-registering the listener
  const deviceContextRef = useRef({
    mediaDevices,
    activeMediaDevices,
    micPermissions,
    camPermissions,
    allowAudioBroadcast,
    allowVideoBroadcast,
    isAudioActive: state.audio.active,
    isVideoActive: state.video.active,
  });

  deviceContextRef.current = {
    mediaDevices,
    activeMediaDevices,
    micPermissions,
    camPermissions,
    allowAudioBroadcast,
    allowVideoBroadcast,
    isAudioActive: state.audio.active,
    isVideoActive: state.video.active,
  };

  const handleRemovedAudioInput = useCallback(
    async (updatedDevices: EnumeratedMediaDevices) => {
      const deviceContext = deviceContextRef.current;

      try {
        const deviceId = getDefaultMediaDevice(updatedDevices, 'audioinput');

        if (deviceContext.isAudioActive) {
          if (deviceContext.allowAudioBroadcast) {
            if (!deviceId) {
              logger.remote().info('[Fast Join] No replacement audio device found');
              deactivateMic();
            } else {
              logger.remote().info('[Fast Join] Auto-switched to new audio device', {
                to: deviceId,
              });

              disableAudio();
              await enableAudio(deviceId);
            }
          } else {
            logger.remote().info('[Fast Join] Audio blocked - no broadcast permission');
            deactivateMic();
          }
        } else if (deviceId) {
          appDispatch(
            activeMediaDeviceUpdated({
              kind: 'audioinput',
              id: deviceId,
            })
          );
        }
      } catch (error) {
        logger.remote().error('[Fast Join] Error handling removed audio device', error);
        Sentry.captureException(error);
        deactivateMic();
      }
    },
    [enableAudio, disableAudio, appDispatch, deactivateMic]
  );

  const handleRemovedVideoInput = useCallback(
    async (updatedDevices: EnumeratedMediaDevices) => {
      const deviceContext = deviceContextRef.current;

      try {
        const deviceId = getDefaultMediaDevice(updatedDevices, 'videoinput');

        if (deviceContext.isVideoActive) {
          if (deviceContext.allowVideoBroadcast) {
            if (!deviceId) {
              logger.remote().info('[Fast Join] No replacement video device found');
              deactivateCamera();
            } else {
              logger.remote().info('[Fast Join] Auto-switched to new video device', {
                to: deviceId,
              });

              await disableCamera();
              await enableCamera(deviceId);
            }
          } else {
            logger.remote().info('[Fast Join] Video blocked - no broadcast permission');
            deactivateCamera();
          }
        } else if (deviceId) {
          appDispatch(
            activeMediaDeviceUpdated({
              kind: 'videoinput',
              id: deviceId,
            })
          );
        }
      } catch (error) {
        logger.remote().error('[Fast Join] Error handling removed video device', error);
        Sentry.captureException(error);
        deactivateCamera();
      }
    },
    [enableCamera, disableCamera, appDispatch, deactivateCamera]
  );

  const handleAddedAudioInput = useCallback(
    async (updatedDevices: EnumeratedMediaDevices) => {
      const deviceContext = deviceContextRef.current;

      try {
        const devicePermissions = getDevicePermissions(updatedDevices, 'audioinput');

        if (devicePermissions === 'granted' && deviceContext.allowAudioBroadcast) {
          const deviceId = getDefaultMediaDevice(updatedDevices, 'audioinput');

          if (!deviceId) {
            logger.remote().info('[Fast Join] No audio device found after device added');
          } else {
            logger.remote().info('[Fast Join] Auto-activated audio device', {
              deviceId,
            });

            await enableAudio(deviceId);
          }
        }
      } catch (error) {
        logger.remote().error('[Fast Join] Error activating added audio device', error);
        Sentry.captureException(error);
        deactivateMic();
      }
    },
    [enableAudio, deactivateMic]
  );

  const handleAddedVideoInput = useCallback(
    async (updatedDevices: EnumeratedMediaDevices) => {
      const deviceContext = deviceContextRef.current;

      try {
        const devicePermissions = getDevicePermissions(updatedDevices, 'videoinput');

        if (devicePermissions === 'granted' && deviceContext.allowVideoBroadcast) {
          const deviceId = getDefaultMediaDevice(updatedDevices, 'videoinput');

          if (!deviceId) {
            logger.remote().info('[Fast Join] No video device found after device added');
          } else {
            logger.remote().info('[Fast Join] Auto-activated video device', {
              deviceId,
            });

            await enableCamera(deviceId);
          }
        }
      } catch (error) {
        logger.remote().error('[Fast Join] Error activating added video device', error);
        Sentry.captureException(error);
        deactivateCamera();
      }
    },
    [enableCamera, deactivateCamera]
  );

  const handleAudioOutputChange = useCallback(
    (updatedDevices: EnumeratedMediaDevices) => {
      const deviceId = getDefaultMediaDevice(updatedDevices, 'audiooutput');

      if (!deviceId) {
        logger.remote().info('[Fast Join] No replacement audio output device found');
        return;
      }

      appDispatch(
        activeMediaDeviceUpdated({
          kind: 'audiooutput',
          id: deviceId,
        })
      );
    },
    [appDispatch]
  );

  const handleDeviceChange = useCallback(async () => {
    const deviceContext = deviceContextRef.current;
    const updatedDevices = await enumerateDevices();
    const { removedActiveDevices, addedDevices } = findDeviceDifferences(
      updatedDevices,
      deviceContext.mediaDevices,
      deviceContext.activeMediaDevices
    );

    if (removedActiveDevices.audioinput) {
      await handleRemovedAudioInput(updatedDevices);
    }

    if (removedActiveDevices.videoinput) {
      await handleRemovedVideoInput(updatedDevices);
    }

    if (deviceContext.micPermissions !== 'granted' && addedDevices.audioinput) {
      await handleAddedAudioInput(updatedDevices);
    }

    if (deviceContext.camPermissions !== 'granted' && addedDevices.videoinput) {
      await handleAddedVideoInput(updatedDevices);
    }

    if (removedActiveDevices.audiooutput || addedDevices.audiooutput) {
      handleAudioOutputChange(updatedDevices);
    }

    appDispatch(
      mediaDevicesUpdated(updatedDevices, {
        updatePermissions: true,
      })
    );
  }, [
    handleRemovedAudioInput,
    handleRemovedVideoInput,
    handleAddedAudioInput,
    handleAddedVideoInput,
    handleAudioOutputChange,
    appDispatch,
  ]);

  const debouncedDeviceChange = useMemo(
    () => debounce(handleDeviceChange, 300),
    [handleDeviceChange]
  );

  useEffect(() => {
    // No listener until the initial request settles: a device-triggered getUserMedia would queue behind it
    if (state.status === 'prompt' || state.status === 'stalled') {
      return undefined;
    }

    addDeviceChangeListener(debouncedDeviceChange);
    return () => {
      removeDeviceChangeListener(debouncedDeviceChange);
      debouncedDeviceChange.cancel();
    };
  }, [debouncedDeviceChange, state.status]);

  useEffect(() => {
    const videoTrack = videoStream?.getVideoTracks()[0];
    if (!videoTrack) {
      return undefined;
    }

    const listener = async () => {
      const updatedDevices = await enumerateDevices();
      const permissions = getDevicePermissions(updatedDevices, 'videoinput');

      if (permissions !== 'granted') {
        appDispatch(
          mediaDevicesUpdated(updatedDevices, {
            updateActiveDevices: true,
            updatePermissions: true,
          })
        );

        await disableCamera();
      }
    };

    videoTrack.addEventListener('ended', listener);

    return () => {
      videoTrack.removeEventListener('ended', listener);
    };
  }, [appDispatch, disableCamera, videoStream]);

  useEffect(() => {
    const audioTrack = audioStream?.getAudioTracks()[0];
    if (!audioTrack) {
      return undefined;
    }

    const listener = async () => {
      const updatedDevices = await enumerateDevices();
      const permissions = getDevicePermissions(updatedDevices, 'audioinput');

      if (permissions !== 'granted') {
        appDispatch(
          mediaDevicesUpdated(updatedDevices, {
            updateActiveDevices: true,
            updatePermissions: true,
          })
        );

        disableAudio();
      }
    };

    audioTrack.addEventListener('ended', listener);

    return () => {
      audioTrack.removeEventListener('ended', listener);
    };
  }, [appDispatch, disableAudio, audioStream]);

  const handleMediaError = async (error: unknown) => {
    if (isDomException(error)) {
      logger.error(`${error.name}: ${error.message}`);

      const audioPromise = broadcastPermissions.audio
        ? requestSingularUserMedia({
            audio: {
              deviceId: {
                exact: getDefaultPromptedDevice(mediaDevices, 'audioinput'),
              },
            },
          })
        : Promise.resolve(undefined);

      const videoPromise = broadcastPermissions.video
        ? requestSingularUserMedia({
            video: {
              deviceId: {
                exact: getDefaultPromptedDevice(mediaDevices, 'videoinput'),
              },
            },
          })
        : Promise.resolve(undefined);

      const [audio, video] = await Promise.all([audioPromise, videoPromise]);
      await updateMediaDevices();

      if (video?.stream) {
        if (mediaDefaults.video) {
          publishingFeed.setJoinMedia(video.stream, 'video');
          dispatch({
            type: 'mediaUpdated',
            payload: { type: 'video', activateMedia: true },
          });
        } else {
          stopStreamTracks(video.stream);
        }
      }

      if (audio?.stream) {
        if (mediaDefaults.audio) {
          publishingFeed.setJoinMedia(audio.stream, 'audio');
          dispatch({
            type: 'mediaUpdated',
            payload: { type: 'audio', activateMedia: true },
          });
        } else {
          stopStreamTracks(audio.stream);
        }
      }

      if (audio?.stream || video?.stream) {
        dispatch({ type: 'statusUpdated', payload: 'success' });
        appDispatch(roomJoinRequest());
      } else if (
        audio?.error.name === 'NotAllowedError' &&
        video?.error.name === 'NotAllowedError'
      ) {
        dispatch({ type: 'statusUpdated', payload: 'denied' });
      } else {
        dispatch({ type: 'statusUpdated', payload: 'failure' });
      }
    } else {
      logger.error(error);
      Sentry.captureException(error);
      dispatch({ type: 'statusUpdated', payload: 'failure' });
    }
  };

  const requestPermissions = async () => {
    try {
      const updatedMediaDevices = await pendingMedia.observe(
        refineMediaDevices(updateMediaDevices, { permissions: broadcastPermissions }),
        'fast-join'
      );

      if (!updatedMediaDevices) {
        return;
      }

      // check against the media defaults from the backend
      // if all values are false, obtain only device permissions and list of devices
      if (!mediaDefaults.video && !mediaDefaults.audio) {
        const tokenUsername = roomToken.parsedData?.u;
        const sdkUsername = sdkInitState.username;

        const predefinedUsername = tokenUsername || sdkUsername;

        if (webrtcAdapter.browserDetails.browser === 'firefox') {
          const constraints = getDevicePromptConstraints(updatedMediaDevices, broadcastPermissions);

          if (constraints.audio === false && constraints.video === false) {
            dispatch({ type: 'statusUpdated', payload: 'denied' });
            return;
          }

          const stream = await getUserMedia(constraints);
          const activeDevices = getDefaultActiveDevices(updatedMediaDevices);

          await updateMediaDevices({
            updatePermissions: true,
            updateActiveDevices: true,
            activeDevices,
          });
          stopStreamTracks(stream);

          if (predefinedUsername) {
            dispatch({ type: 'statusUpdated', payload: 'failure' });
          } else {
            dispatch({ type: 'statusUpdated', payload: 'success' });
            appDispatch(roomJoinRequest());
          }
          return;
        }

        const activeDevices = getDefaultActiveDevices(updatedMediaDevices);

        await updateMediaDevices({
          updatePermissions: true,
          updateActiveDevices: true,
          activeDevices,
        });

        if (predefinedUsername) {
          dispatch({ type: 'statusUpdated', payload: 'failure' });
        } else {
          dispatch({ type: 'statusUpdated', payload: 'success' });
          appDispatch(roomJoinRequest());
        }

        return;
      }

      const constraints = getDevicePromptConstraints(updatedMediaDevices, broadcastPermissions);

      // If no valid constraints, we can't get any media
      if (constraints.audio === false && constraints.video === false) {
        dispatch({ type: 'statusUpdated', payload: 'denied' });
        return;
      }

      const stream = await getUserMedia(constraints);

      const activeDevices = getDefaultActiveDevices(mediaDevices);

      await updateMediaDevices({
        updatePermissions: true,
        updateActiveDevices: true,
        activeDevices,
      });

      if (mediaDefaults.video) {
        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack) {
          const newVideoStream = new MediaStream();
          newVideoStream.addTrack(videoTrack.clone());

          const settings = videoTrack.getSettings();

          if (settings.facingMode) {
            appDispatch(facingModeChanged(settings.facingMode));
          }
          publishingFeed.setJoinMedia(newVideoStream, 'video');
          dispatch({
            type: 'mediaUpdated',
            payload: { type: 'video', activateMedia: true },
          });
        }
      }

      if (mediaDefaults.audio) {
        const audioTrack = stream.getAudioTracks()[0];
        if (audioTrack) {
          const newAudioStream = new MediaStream();
          newAudioStream.addTrack(audioTrack.clone());
          publishingFeed.setJoinMedia(newAudioStream, 'audio');
          dispatch({
            type: 'mediaUpdated',
            payload: { type: 'audio', activateMedia: true },
          });
        }
      }

      stopStreamTracks(stream);

      dispatch({ type: 'statusUpdated', payload: 'success' });

      appDispatch(roomJoinRequest());
    } catch (error) {
      await handleMediaError(error);
    }
  };

  return {
    requestPermissions,
    state,
    joinWithoutMedia: pendingMedia.joinWithoutMedia,
    reload: pendingMedia.reload,
  };
};
